import { useUserInfo } from '@/hooks';
import { spin } from '@/utils';
import {
  Add as AddIcon,
  Autorenew as AutorenewIcon,
  Done as DoneIcon,
} from '@mui/icons-material';
import {
  alpha,
  Autocomplete,
  Avatar,
  Box,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { IntelligenceItem } from './IntelligenceItem';
import { IntelligenceItemEditor } from './IntelligenceItemEditor';
import { useIntelligenceItemCategories } from './useIntelligenceItemCategories';
import { useIntelligenceItems } from './useIntelligenceItems';

export function PriorityIntelligence({ title, groupCodes, onComplete }) {
  const {
    query: { data: items, isFetching, refetch },
  } = useIntelligenceItems(groupCodes);
  const {
    query: { data: categories },
  } = useIntelligenceItemCategories();
  const {
    query: {
      data: { briefingRole },
    },
  } = useUserInfo();
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [editCode, setEditCode] = useState(null);
  const filteredItems = items.filter((item) =>
    selectedCategories.length > 0
      ? item.categories?.some((category) =>
          selectedCategories.includes(category),
        )
      : true,
  );

  const handleSettingsClick = (code) => () => {
    setEditCode(code);
  };

  function handleRefreshClick() {
    refetch();
  }

  function handleAddClick() {
    setEditCode('new');
  }

  function onEditorClose() {
    setEditCode(null);
  }

  function handleCompleteClick() {
    onComplete();
  }

  function renderInput(params) {
    return <TextField label="Categories" {...params} />;
  }

  function handleChange(_, selections) {
    setSelectedCategories(
      selections.map((selection) => selection.value ?? selection),
    );
  }

  return (
    <Box sx={{ px: 1 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={(theme) => ({
          alignItems: 'center',
          position: 'sticky',
          top: 48,
          bgcolor: alpha(theme.palette.background.paper, 0.8),
          zIndex: 700,
          pl: 1,
          py: 1,
        })}
      >
        <Typography variant="h6" sx={{ flexShrink: 0 }}>
          {title}
        </Typography>
        <Box>
          <Tooltip title="Done" placement="right">
            <IconButton onClick={handleCompleteClick}>
              <DoneIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
        <Autocomplete
          value={selectedCategories}
          size="small"
          fullWidth
          multiple
          // selectOnFocus={selectOnFocus}
          // clearOnBlur={clearOnBlur}
          // handleHomeEndKeys={handleHomeEndKeys}
          onChange={handleChange}
          options={categories ?? []}
          renderInput={renderInput}
        />
        <Box sx={{ flexShrink: 0 }}>
          <Tooltip title="Refresh" placement="bottom">
            <Box component="span">
              <IconButton onClick={handleRefreshClick} disabled={isFetching}>
                <AutorenewIcon
                  fontSize="inherit"
                  sx={
                    isFetching
                      ? { animation: `${spin} 2s linear infinite` }
                      : undefined
                  }
                />
              </IconButton>
            </Box>
          </Tooltip>
          {briefingRole === 'ADMIN' && (
            <Tooltip title="Add" placement="left">
              <IconButton size="small" onClick={handleAddClick}>
                <Avatar
                  sx={{
                    color: 'secondary.contrastText',
                    bgcolor: 'secondary.main',
                    width: 32,
                    height: 32,
                    fontSize: 24,
                  }}
                >
                  <AddIcon fontSize="inherit" />
                </Avatar>
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Stack>
      {!isFetching && filteredItems.length === 0 && (
        <Typography variant="body2" color="textSecondary" sx={{ px: 1 }}>
          No items
        </Typography>
      )}
      <Stack spacing={1} sx={{ pb: 1 }}>
        {filteredItems.map((item) => (
          <IntelligenceItem
            key={item.code}
            item={item}
            onSettingsClick={handleSettingsClick(item.code)}
          />
        ))}
      </Stack>
      <IntelligenceItemEditor
        code={editCode}
        isOpen={Boolean(editCode)}
        onClose={onEditorClose}
        groupCodes={groupCodes}
      />
    </Box>
  );
}
